/**
 * Session-expired signal — the non-React seam between the API layer and the re-auth UI.
 *
 * A dead Auth0 session (the rotating refresh token expired or was revoked) makes the token getter
 * return null, so requests go out with no Bearer token and the backend answers 401. Without this seam
 * that 401 renders as a generic "Something went wrong" card whose Retry just re-fails on the same dead
 * token — the user's only escape is a fresh browser/incognito (localStorage, which "clear cache" never
 * touches, holds the dead session). This module lets the QueryCache/MutationCache onError hooks raise a
 * single blocking "Session expired — sign in" overlay instead.
 *
 * A null token is NOT by itself proof the session is dead, though — a Web Locks timeout or an offline
 * blip produces exactly the same null. So the signal also carries the token getter's classification of
 * that failure (`renewalFailure`, from tokenRenewal); the handler uses it to decide between one more
 * silent retry and ejecting the user.
 *
 * Deliberately React-free (mirrors setTokenGetter in api/client): the QueryClient is built at module
 * scope, before any provider mounts, so the detector cannot be a hook. `SessionExpiredProvider`
 * registers the real handler; until it does, a signal is parked in `pending` and replayed on register
 * (a query can fail before the provider's effect runs).
 */

import { ApiRequestError } from "@/api/client";
import type { RenewalFailure } from "./tokenRenewal";

export interface SessionExpiredSignal {
  /**
   * Whether the failed request carried a Bearer token. `true` = a token was sent and the backend
   * rejected it (possibly transient: Auth0 signing-key rotation, clock skew, a backend restart window)
   * → the handler does one silent-refresh confirmation before ejecting a maybe-valid user. `false` = no
   * token was sent, because the token getter returned null — read `renewalFailure` for WHY.
   */
  tokenWasSent: boolean;
  /**
   * Why the silent renewal failed, when no token was sent — the field that stops a self-healing blip
   * from reading as a dead session. `"dead"` = Auth0 said the session needs an interactive login
   * (login_required / invalid_grant / missing_refresh_token / …) → eject immediately. `"transient"` =
   * a timeout, a Web Locks contention, a network drop, a 429, or an unrecognised error → worth ONE
   * silent retry before ejecting. `null` = nothing to consult (the token was sent and the backend
   * rejected it, no fresh record existed when the request went out, or the signal came from a user
   * clicking "Sign in"). Sourced from `ApiError.renewalFailure`, i.e. captured when the request was
   * built — never re-read at 401 time, where a concurrent renewal may already have cleared it.
   */
  renewalFailure: RenewalFailure | null;
  /**
   * WHO raised this signal: `true` = a human pressed a "Sign in" button; absent = it came from a 401.
   *
   * Both travel this one channel, and until the transient recovery existed that was harmless — a
   * no-token 401 ejected immediately, so the handler's in-flight gate (`busyRef`) was essentially never
   * closed when a click arrived. The recovery closes it on EVERY transient no-token 401, for as long as
   * the silent refresh runs, and a gate that drops "every signal while we're busy" then drops the click
   * too: the user sits on the same 401 error cards, presses Sign in, and nothing whatsoever happens.
   *
   * So the handler checks this BEFORE that gate and shows the overlay outright. A background refresh is
   * not entitled to decide on the user's behalf that they didn't mean it. Deliberately optional and
   * `true`-only rather than a two-valued flag: absence means "came from a 401", so `reportIfSessionExpired`
   * and every other producer stays exactly as it was, and no future 401 path can set it by accident.
   */
  userInitiated?: true;
}

type Handler = (signal: SessionExpiredSignal) => void;

let handler: Handler | null = null;
let pending: SessionExpiredSignal | null = null;

/** Register (or clear, with null) the handler that reacts to an expired session. Drains any signal
 *  that arrived before registration so a first-paint 401 is never lost. */
export function setSessionExpiredHandler(fn: Handler | null): void {
  handler = fn;
  if (fn && pending) {
    const parked = pending;
    pending = null;
    fn(parked);
  }
}

/** Announce that the session appears dead. Idempotency + the transient-vs-dead decision live in the
 *  handler; this only routes (or parks) the signal. */
export function signalSessionExpired(signal: SessionExpiredSignal): void {
  if (handler) handler(signal);
  else pending = signal;
}

/**
 * The single 401 detector wired into the QueryCache and MutationCache onError hooks. Strictly gated on
 * status === 401 so it never fires on a 403 (roster/permission — handled by NoAccess), a 404, a
 * status-0 network error, or a 500 server bug; and the sync-now / roster-sync structured envelopes
 * (429/502/409/503) never reach here at all — the client returns them as data rather than throwing.
 *
 * When no token was sent it also forwards the token getter's own verdict, so the handler can tell "the
 * refresh token is gone" from "the renewal timed out on a lock a second ago". That verdict is READ OFF
 * THE ERROR, not looked up here: api/client captured it the instant the getter returned null, because
 * the record is one global slot that a different, later renewal can legitimately clear before this 401
 * arrives — which is precisely the concurrent case the recovery exists for. When a token WAS sent the
 * renewal plainly succeeded (the backend rejected the token), so the field is null and the existing
 * token-present path handles it.
 *
 * Returns whether it signalled (for tests).
 */
export function reportIfSessionExpired(error: unknown): boolean {
  if (error instanceof ApiRequestError && error.error.status === 401) {
    const tokenWasSent = error.error.authAttempted === true;
    signalSessionExpired({
      tokenWasSent,
      renewalFailure: tokenWasSent ? null : (error.error.renewalFailure ?? null),
    });
    return true;
  }
  return false;
}

/** TEST-ONLY: drop any registered handler + parked signal so cases don't leak across each other. */
export function __resetSessionExpiredForTest(): void {
  handler = null;
  pending = null;
}
